import { Login, Logout, SwapHoriz } from '@mui/icons-material'
import {
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { FileDragAndDropzone, useMinimalsTheme } from '@tom-ui/ui'
import { FormField, useFormStore } from '@tom-ui/utils'
import { GateDirection, LayoutObjectType } from '@yard/app/api'
import { FormProps } from '@yard/app/models/form-props.model'
import { observer } from 'mobx-react-lite'
import { useDropzone } from 'react-dropzone'
import { ILayoutObjectFormData } from '../../models/yard-setup.model'
import { LayoutObjectViewStore } from '../../stores/nodes/layout-object.view-store'

interface Props extends FormProps {
  layoutObjectViewStore?: LayoutObjectViewStore
  onSubmit: (formValues: ILayoutObjectFormData) => Promise<void> | void
}

export const LayoutObjectForm = observer(({ id, layoutObjectViewStore, onSubmit }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const layoutObject = layoutObjectViewStore?.focusedLayoutObject

  const formStore = useFormStore<ILayoutObjectFormData>()

  const handleSubmit = () => {
    if (formStore.validate()) {
      onSubmit(formStore.data)
    }
  }

  return (
    <Stack
      id={id}
      component='form'
      autoComplete='off'
      noValidate
      onSubmit={event => {
        event.preventDefault()
        handleSubmit()
      }}
      spacing={theme.customSpacing.m}
    >
      <FormField
        formStore={formStore}
        name={'name'}
        initialValue={layoutObject?.name ?? ''}
        validate={value => {
          if (!value) return t('fieldIsRequired', 'Field is required')
        }}
      >
        {store => (
          <Stack>
            <TextField
              {...store.props}
              fullWidth
              label={`${t('name', 'Name')}`}
              required
              name='name'
            />
            {store.error && (
              <Typography variant='caption' color='error'>
                {store.error}
              </Typography>
            )}
          </Stack>
        )}
      </FormField>

      <FormField
        formStore={formStore}
        name={'description'}
        initialValue={layoutObject?.description ?? ''}
      >
        {store => (
          <TextField
            {...store.props}
            fullWidth
            multiline
            minRows={2}
            label={t('description', 'Description')}
            name='description'
          />
        )}
      </FormField>

      {layoutObjectViewStore?.editingType === LayoutObjectType.Gate && (
        <>
          <FormField
            formStore={formStore}
            name={'code'}
            initialValue={layoutObject?.code ?? ''}
            validate={value => {
              if (value && layoutObjectViewStore?.isGateCodeTaken(value)) {
                return t('gateCodeMustBeUnique', 'Code must be unique')
              }
            }}
          >
            {store => (
              <Stack>
                <TextField
                  {...store.props}
                  fullWidth
                  label={t('code', 'Code')}
                  name='code'
                  error={!!store.error}
                />
                {store.error && (
                  <Typography variant='caption' color='error'>
                    {store.error}
                  </Typography>
                )}
              </Stack>
            )}
          </FormField>

          <FormField<ILayoutObjectFormData, GateDirection>
            formStore={formStore}
            name={'direction'}
            initialValue={layoutObject?.direction ?? GateDirection.Both}
          >
            {store => (
              <Stack spacing={1}>
                <Typography variant='body2'>{t('direction', 'Direction')}</Typography>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={store.value}
                  onChange={(_, value: GateDirection | null) => {
                    if (value !== null) store.setValue(value)
                  }}
                >
                  <ToggleButton value={GateDirection.In}>
                    <Tooltip title={t('gateDirectionIn', 'In')}>
                      <Login fontSize='small' />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value={GateDirection.Out}>
                    <Tooltip title={t('gateDirectionOut', 'Out')}>
                      <Logout fontSize='small' />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value={GateDirection.Both}>
                    <Tooltip title={t('gateDirectionBoth', 'Both')}>
                      <SwapHoriz fontSize='small' />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}
          </FormField>

          <FormField<ILayoutObjectFormData, boolean>
            formStore={formStore}
            name={'active'}
            initialValue={layoutObject?.active ?? true}
          >
            {store => (
              <FormControlLabel
                control={
                  <Switch
                    checked={store.value}
                    onChange={(_, checked) => store.setValue(checked)}
                  />
                }
                label={t('active', 'Active')}
              />
            )}
          </FormField>
        </>
      )}

      {layoutObjectViewStore?.editingType !== LayoutObjectType.Gate && (
        <FormField
          formStore={formStore}
          name={'color'}
          initialValue={layoutObject?.color ?? '#bdbdbd'}
        >
          {store => (
            <TextField
              {...store.props}
              fullWidth
              type='color'
              label={t('color', 'Color')}
              name='color'
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          )}
        </FormField>
      )}

      {layoutObjectViewStore?.editingType === LayoutObjectType.Background && (
        <>
          <FormField<ILayoutObjectFormData, number>
            formStore={formStore}
            name={'opacity'}
            initialValue={Math.round((layoutObject?.opacity ?? 1) * 100)}
          >
            {store => (
              <Stack spacing={1}>
                <Typography variant='body2'>
                  {t('opacity', 'Opacity')}: {store.value}%
                </Typography>
                <Slider
                  value={store.value}
                  min={0}
                  max={100}
                  onChange={(_, value) => store.setValue(value as number)}
                />
              </Stack>
            )}
          </FormField>

          {!layoutObject && (
            <FormField<ILayoutObjectFormData, File | undefined>
              initialValue={undefined}
              formStore={formStore}
              name={'file'}
            >
              {store => {
                const { getRootProps, getInputProps } = useDropzone({
                  maxFiles: 1,
                  accept: {
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpeg', '.jpg'],
                  },
                  onDrop: (acceptedFiles: File[]) => {
                    if (acceptedFiles.length > 0) {
                      store.setValue(acceptedFiles[0])
                    }
                  },
                })

                return (
                  <FileDragAndDropzone
                    file={store.value}
                    inputProps={getInputProps()}
                    rootProps={getRootProps()}
                    setFile={(file?: File) => store.setValue(file)}
                  />
                )
              }}
            </FormField>
          )}
        </>
      )}
    </Stack>
  )
})
